import React, { useState, useEffect, useCallback, useMemo } from 'react';
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AnimatePresence, motion } from 'framer-motion';

// Auth
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import LoginPage from '@/components/LoginPage';
import AdminConsole from '@/components/AdminConsole';

// Components
import DiscoveryWorkbench from '@/components/DiscoveryWorkbench';
import FinalReportPanel from '@/components/FinalReportPanel';
import AssessmentDashboard from '@/components/AssessmentDashboard';
import ComparisonView from '@/components/ComparisonView';
import PrefillDialog from '@/components/PrefillDialog';

// Config
import { QUESTION_CONFIG, SECTION_ORDER, getQuestionsBySection } from '@/config/question-config';
import { SECTION_CONFIG } from '@/config/domain-weights';

// Lib
import { 
  createDefaultState, 
  saveAssessment, 
  loadAssessment, 
  hasAssessment, 
  resetAssessment,
  autoSaveAssessment
} from '@/lib/storage';

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-slate-600 border-t-white rounded-full animate-spin mx-auto"></div>
      <p className="text-slate-400 mt-4">Loading DefencIQ...</p>
    </div>
  </div>
);

// Main app content (authenticated)
const AppContent = () => {
  const { user, loading, isAuthenticated, isAdmin, logout, authFetch } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' | 'workbench' | 'compare'
  const [compareIds, setCompareIds] = useState([]);
  
  // Backend assessment ID (null if not yet synced)
  const [backendId, setBackendId] = useState(() => {
    const saved = loadAssessment();
    return saved?.backendId || null;
  });
  
  // Initialize state from localStorage if available (lazy initializers)
  const [engagement, setEngagement] = useState(() => {
    const saved = loadAssessment();
    return saved?.engagement || {
      assessment_id: `DIQ-${Date.now()}`,
      client_name: '',
      assessor_name: user?.name || '',
      assessment_date: new Date().toISOString().split('T')[0],
      engagement_type: '',
      notes: ''
    };
  });
  const [answers, setAnswers] = useState(() => {
    const saved = loadAssessment();
    return saved?.answers || {};
  });
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = loadAssessment();
    if (saved?.currentSection) {
      const idx = SECTION_ORDER.indexOf(saved.currentSection);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // ===== Pre-fill from previous assessment state =====
  // prefilledIds: question field_ids that were seeded from a previous
  // assessment and have NOT yet been touched by the consultant. Rendered
  // as a "Carried over from [date]" pill and drives the mandatory review
  // checkbox at submit time.
  const [prefilledIds, setPrefilledIds] = useState(() => new Set());
  const [prefillSourceDate, setPrefillSourceDate] = useState(null);
  const [prefillReviewed, setPrefillReviewed] = useState(false);
  // Track which client names we've already prompted for in this session
  // to prevent re-triggering the dialog after user picks Start Fresh.
  const [promptedClients, setPromptedClients] = useState(() => new Set());
  const [prefillDialog, setPrefillDialog] = useState({ open: false, candidates: [] });
  const prefillTriggerTimer = React.useRef(null);

  // Helper to safely parse authFetch responses (handles body-stream-consumed)
  const safeParse = async (response) => {
    try {
      const text = await response.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  // Sync assessment to backend
  const syncToBackend = useCallback(async (engData, answersData, currentBackendId, currentStatus) => {
    if (!isAuthenticated || !engData.client_name) return currentBackendId;
    
    try {
      if (currentBackendId) {
        // Preserve 'completed' status — don't downgrade on auto-save
        const status = currentStatus === 'completed'
          ? 'completed'
          : Object.keys(answersData).length > 0 ? 'in_progress' : 'draft';

        const res = await authFetch(`/api/assessments/${currentBackendId}`, {
          method: 'PUT',
          body: JSON.stringify({
            client_name: engData.client_name,
            engagement_type: engData.engagement_type || null,
            notes: engData.notes || null,
            client_logo_base64: engData.client_logo_base64 ?? null,
            status,
            answers: answersData,
          }),
        });
        if (res.ok) return currentBackendId;
        // If 404, the assessment was deleted — create a new one
        if (res.status === 404) {
          return syncToBackend(engData, answersData, null, null);
        }
      } else {
        // Create new assessment
        const res = await authFetch('/api/assessments/', {
          method: 'POST',
          body: JSON.stringify({
            client_name: engData.client_name || 'Untitled Assessment',
            engagement_type: engData.engagement_type || null,
            notes: engData.notes || null,
            client_logo_base64: engData.client_logo_base64 || null,
          }),
        });
        if (res.ok) {
          const data = await safeParse(res);
          if (data?.id) {
            // Now update with answers
            if (Object.keys(answersData).length > 0) {
              await authFetch(`/api/assessments/${data.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                  status: 'in_progress',
                  answers: answersData,
                }),
              });
            }
            return data.id;
          }
        }
      }
    } catch (err) {
      console.error('Backend sync failed:', err);
    }
    return currentBackendId;
  }, [isAuthenticated, authFetch]);

  // Load an existing assessment from backend into the workbench
  const loadAssessmentFromBackend = useCallback(async (assessmentId) => {
    try {
      const res = await authFetch(`/api/assessments/${assessmentId}`);
      let data;
      try {
        const text = await res.text();
        data = JSON.parse(text);
      } catch {
        data = null;
      }
      if (res.ok && data) {
        setBackendId(data.id);
        setEngagement({
          assessment_id: data.id,
          client_name: data.client_name || '',
          assessor_name: data.user_name || user?.name || '',
          assessment_date: data.created_at ? new Date(data.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          engagement_type: data.engagement_type || '',
          notes: data.notes || '',
          client_logo_base64: data.client_logo_base64 || ''
        });
        setAnswers(data.answers || {});
        setPrefilledIds(new Set());
        setPrefillSourceDate(null);
        setPrefillReviewed(false);
        setCurrentStep(0);
        setSubmitted(data.status === 'completed');
        setCurrentView('workbench');
        
        // Also save to localStorage for continuity
        saveAssessment({
          engagement: {
            assessment_id: data.id,
            client_name: data.client_name || '',
            assessor_name: data.user_name || '',
            assessment_date: data.created_at ? new Date(data.created_at).toISOString().split('T')[0] : '',
            engagement_type: data.engagement_type || '',
            notes: data.notes || '',
            client_logo_base64: data.client_logo_base64 || ''
          },
          answers: data.answers || {},
          backendId: data.id,
          version: '1.0.0'
        });
        
        toast.success('Assessment loaded', { description: data.client_name });
      } else {
        toast.error('Failed to load assessment');
      }
    } catch (err) {
      console.error('Failed to load assessment:', err);
      toast.error('Failed to load assessment');
    }
  }, [authFetch, user]);

  // Start a new blank assessment
  const handleNewAssessment = useCallback(() => {
    resetAssessment();
    setBackendId(null);
    setEngagement({
      assessment_id: `DIQ-${Date.now()}`,
      client_name: '',
      assessor_name: user?.name || '',
      assessment_date: new Date().toISOString().split('T')[0],
      engagement_type: '',
      notes: ''
    });
    setAnswers({});
    setPrefilledIds(new Set());
    setPrefillSourceDate(null);
    setPrefillReviewed(false);
    setPromptedClients(new Set());
    setCurrentStep(0);
    setSubmitted(false);
    setCurrentView('workbench');
  }, [user]);

  // Go back to dashboard
  const handleBackToDashboard = useCallback(() => {
    setCurrentView('dashboard');
  }, []);

  // Update assessor name when user changes
  useEffect(() => {
    if (user?.name && !engagement.assessor_name) {
      setEngagement(prev => ({
        ...prev,
        assessor_name: user.name
      }));
    }
  }, [user, engagement.assessor_name]);

  // Build sections from config
  const sections = useMemo(() => {
    return SECTION_ORDER.map(id => ({
      id,
      ...SECTION_CONFIG[id],
      questions: getQuestionsBySection(id)
    }));
  }, []);

  // Show toast if draft was restored on mount
  useEffect(() => {
    const saved = loadAssessment();
    if (saved?.engagement?.client_name && isAuthenticated) {
      toast.success('Draft restored', {
        description: `Continuing assessment for ${saved.engagement.client_name}`
      });
    }
  }, [isAuthenticated]);

  // Auto-save on state changes (debounced in storage.js)
  const backendSyncTimer = React.useRef(null);
  useEffect(() => {
    if (isAuthenticated) {
      autoSaveAssessment({
        engagement,
        answers,
        currentSection: SECTION_ORDER[currentStep],
        backendId,
        version: '1.0.0'
      });
      
      // Debounced backend sync (3 seconds)
      if (backendSyncTimer.current) clearTimeout(backendSyncTimer.current);
      backendSyncTimer.current = setTimeout(async () => {
        const newId = await syncToBackend(engagement, answers, backendId, submitted ? 'completed' : null);
        if (newId && newId !== backendId) {
          setBackendId(newId);
          // Persist backendId to localStorage too
          saveAssessment({
            engagement,
            answers,
            currentSection: SECTION_ORDER[currentStep],
            backendId: newId,
            version: '1.0.0'
          });
        }
      }, 3000);
    }
    return () => {
      if (backendSyncTimer.current) clearTimeout(backendSyncTimer.current);
    };
  }, [engagement, answers, currentStep, isAuthenticated, backendId, syncToBackend]);

  // Update answer for a specific section/field
  const updateSectionField = useCallback((sectionId, fieldId, payload) => {
    setAnswers(prev => ({
      ...prev,
      [fieldId]: payload
    }));
    // Any manual edit removes the "carried over" flag for this question.
    setPrefilledIds(prev => {
      if (!prev.has(fieldId)) return prev;
      const next = new Set(prev);
      next.delete(fieldId);
      return next;
    });
  }, []);

  // Update engagement field
  const updateEngagementField = useCallback((fieldId, value) => {
    setEngagement(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }, []);

  // ===== Pre-fill trigger + accept / dismiss handlers =====
  // Debounced lookup: when a consultant enters a client_name for a brand-new
  // assessment (no backendId, no answers yet), check whether we have past
  // assessments on file for that client and offer to pre-fill.
  useEffect(() => {
    if (prefillTriggerTimer.current) clearTimeout(prefillTriggerTimer.current);
    const name = (engagement.client_name || '').trim();
    if (!isAuthenticated || !name) return;
    // Only prompt when this is a fresh authoring session
    if (backendId) return;
    if (Object.keys(answers).length > 0) return;
    if (promptedClients.has(name.toLowerCase())) return;

    prefillTriggerTimer.current = setTimeout(async () => {
      try {
        const res = await authFetch(
          `/api/assessments/prefill-candidates?client_name=${encodeURIComponent(name)}`
        );
        if (!res.ok) return;
        const candidates = await safeParse(res);
        if (Array.isArray(candidates) && candidates.length > 0) {
          setPrefillDialog({ open: true, candidates });
        }
        // Mark as prompted regardless (avoids repeated dialogs while typing)
        setPromptedClients(prev => {
          const next = new Set(prev);
          next.add(name.toLowerCase());
          return next;
        });
      } catch (err) {
        console.error('Prefill lookup failed:', err);
      }
    }, 800);

    return () => {
      if (prefillTriggerTimer.current) clearTimeout(prefillTriggerTimer.current);
    };
  }, [engagement.client_name, isAuthenticated, backendId, answers, promptedClients, authFetch]);

  const handlePrefillAccept = useCallback(async (assessmentId, sourceDate) => {
    try {
      const res = await authFetch(`/api/assessments/${assessmentId}`);
      const data = await safeParse(res);
      if (!res.ok || !data?.answers) {
        toast.error('Failed to pre-fill answers');
        setPrefillDialog({ open: false, candidates: [] });
        return;
      }
      setAnswers(data.answers);
      setPrefilledIds(new Set(Object.keys(data.answers)));
      setPrefillSourceDate(sourceDate);
      setPrefillReviewed(false);
      setPrefillDialog({ open: false, candidates: [] });
      toast.success('Answers pre-filled', {
        description: `Carried over from ${sourceDate}. Each answer is tagged and editable.`
      });
    } catch (err) {
      console.error('Prefill accept failed:', err);
      toast.error('Failed to pre-fill answers');
      setPrefillDialog({ open: false, candidates: [] });
    }
  }, [authFetch]);

  const handlePrefillDismiss = useCallback(() => {
    setPrefillDialog({ open: false, candidates: [] });
  }, []);

  const handlePrefillStartFresh = useCallback(() => {
    setPrefillDialog({ open: false, candidates: [] });
  }, []);

  // Navigation
  const goNext = useCallback(() => {
    if (currentStep < sections.length - 1) {
      setCurrentStep(s => s + 1);
    }
  }, [currentStep, sections.length]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
    }
  }, [currentStep]);

  const goToSection = useCallback((index) => {
    setCurrentStep(index);
  }, []);

  const goToSectionById = useCallback((sectionId) => {
    const idx = SECTION_ORDER.indexOf(sectionId);
    if (idx >= 0) {
      setCurrentStep(idx);
    }
  }, []);

  // Submit/complete
  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    setShowReportModal(true);
    toast.success('Assessment complete', {
      description: 'Viewing full report'
    });
    // Sync completed status to backend
    if (backendId) {
      try {
        await authFetch(`/api/assessments/${backendId}`, {
          method: 'PUT',
          body: JSON.stringify({
            status: 'completed',
            answers,
          }),
        });
      } catch (err) {
        console.error('Failed to sync completed status:', err);
      }
    } else {
      // Create and mark as completed
      const newId = await syncToBackend(engagement, answers, null, null);
      if (newId) {
        setBackendId(newId);
        try {
          await authFetch(`/api/assessments/${newId}`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'completed', answers }),
          });
        } catch {}
      }
    }
  }, [backendId, authFetch, answers, engagement, syncToBackend]);

  // Open report modal
  const handleViewReport = useCallback(() => {
    setShowReportModal(true);
  }, []);

  // Close report modal
  const handleCloseReport = useCallback(() => {
    setShowReportModal(false);
  }, []);

  // Save draft manually
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    
    // Save to localStorage
    const localSuccess = saveAssessment({
      engagement,
      answers,
      currentSection: SECTION_ORDER[currentStep],
      backendId,
      version: '1.0.0'
    });
    
    // Sync to backend
    const newId = await syncToBackend(engagement, answers, backendId, submitted ? 'completed' : null);
    if (newId && newId !== backendId) {
      setBackendId(newId);
      saveAssessment({
        engagement,
        answers,
        currentSection: SECTION_ORDER[currentStep],
        backendId: newId,
        version: '1.0.0'
      });
    }
    
    setIsSaving(false);
    if (localSuccess) {
      toast.success('Draft saved');
    } else {
      toast.error('Failed to save draft');
    }
  }, [engagement, answers, currentStep, backendId, syncToBackend]);

  // Reset assessment
  const handleReset = useCallback(() => {
    if (window.confirm('Are you sure you want to reset this assessment? All data will be lost.')) {
      resetAssessment();
      setBackendId(null);
      setEngagement({
        assessment_id: `DIQ-${Date.now()}`,
        client_name: '',
        assessor_name: user?.name || '',
        assessment_date: new Date().toISOString().split('T')[0],
        engagement_type: '',
        notes: ''
      });
      setAnswers({});
      setPrefilledIds(new Set());
      setPrefillSourceDate(null);
      setPrefillReviewed(false);
      setPromptedClients(new Set());
      setCurrentStep(0);
      setSubmitted(false);
      toast.success('Assessment reset');
    }
  }, [user]);

  // Show loading while checking auth
  if (loading) {
    return <LoadingSpinner />;
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show admin console if admin clicked the button
  if (showAdmin && isAdmin) {
    return <AdminConsole onClose={() => setShowAdmin(false)} />;
  }

  // Show dashboard as the default landing page
  if (currentView === 'dashboard') {
    return (
      <div className="App">
        <AssessmentDashboard
          onOpenAssessment={loadAssessmentFromBackend}
          onNewAssessment={handleNewAssessment}
          onOpenAdmin={() => setShowAdmin(true)}
          isAdmin={isAdmin}
          onLogout={logout}
          onCompare={(ids) => { setCompareIds(ids); setCurrentView('compare'); }}
        />
        <Toaster position="bottom-right" theme="light" />
      </div>
    );
  }

  // Show comparison view
  if (currentView === 'compare' && compareIds.length === 2) {
    return (
      <div className="App">
        <ComparisonView
          assessmentIds={compareIds}
          onBack={handleBackToDashboard}
        />
        <Toaster position="bottom-right" theme="light" />
      </div>
    );
  }

  return (
    <div className="App">
      <DiscoveryWorkbench
        sections={sections}
        currentStep={currentStep}
        engagement={engagement}
        answers={answers}
        submitted={submitted}
        isSaving={isSaving}
        onUpdateSectionField={updateSectionField}
        onUpdateEngagementField={updateEngagementField}
        onGoNext={goNext}
        onGoBack={goBack}
        onGoToSection={goToSection}
        onGoToSectionById={goToSectionById}
        onSubmit={handleSubmit}
        onSave={handleSave}
        onReset={handleReset}
        onViewReport={handleViewReport}
        // Pre-fill props
        prefilledIds={prefilledIds}
        prefillSourceDate={prefillSourceDate}
        prefillReviewed={prefillReviewed}
        onSetPrefillReviewed={setPrefillReviewed}
        // Auth props
        user={user}
        isAdmin={isAdmin}
        onLogout={logout}
        onOpenAdmin={() => setShowAdmin(true)}
        onBackToDashboard={handleBackToDashboard}
      />

      <PrefillDialog
        open={prefillDialog.open}
        clientName={engagement.client_name}
        candidates={prefillDialog.candidates}
        onPrefill={handlePrefillAccept}
        onStartFresh={handlePrefillStartFresh}
        onDismiss={handlePrefillDismiss}
      />
      
      {/* Full Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto"
            data-testid="report-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="min-h-screen"
            >
              <FinalReportPanel
                engagement={engagement}
                answers={answers}
                onBack={handleCloseReport}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
